import {AfterContentInit, Component, EventEmitter, OnDestroy, ViewChild} from '@angular/core';
import {Observable, Subscription}                                        from 'rxjs';
import {IApPermissionData, IGridPagerData, IWizardChoiceData}            from '../../../../ap-interface/interfaces';
import {ApGridPagerType}                                                 from '../../../../ap-interface/enums';
import {ApPermissions}                                                   from 'invoker-transport';
import {
  ApUsersEditComponent
}                                                                        from '../../../../entry-components/ap-settings-entry-components/ap-users-edit.component';
import {
  ApDynComponentComponent
}                                                                        from '../../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                        from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                        from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                                        from '../../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig
}                                                                        from '../../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                        from '../../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfig
}                                                                        from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {GetBooleanPipe}                                                  from '../../../../ap-utils';
import {
  FarmUsersStore
}                                                                        from '../../../../stores/farm/farm.users.store';
import {FormStore}                                                       from '../../../../stores/layout/form.store';
import {MapViewStore}                                                    from '../../../../stores/layout/mapview.store';
import {
  TranslationStore
}                                                                        from '../../../../stores/translation/translation.store';
import {
  GetPermissionPipe
}                                                                        from '../../../../ap-permission/pipes/get-permission.pipe';
import {LoginStore}                                                      from '../../../../stores/login/login.store';
import IFarmUsers = Data.Authentication.IFarmUsers;
import IUsergroupUser = Data.AccessPermission.IUsergroupUser;
import {
  ApRoleTypeService
}                                                                        from '../../../../services/common/ap-role-type.service';

enum UserEdits {
  IsActive = 0,
  Remove = 1
}

enum UserEditGridActions {
  ACTIVE = 1,
  INACTIVE = 0
}

@Component({
  selector: 'ap-users',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="items"
                     [loading$]="farmUsersLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [canSearch]="true"
                     (create)="onCreate()"
                     [canCreate]="(ApPermissions.EDIT_USERS | getPermission | async)"
                     (edit)="onEdit($event)">
    </ap-dyncomponent>`
})

export class ApUsersComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  columns: ApDynGridColumnConfigBase[] = [];
  items: any[] = [];
  pager: ApDynGridPagerConfigBase;
  farmUsersLoading$: Observable<boolean>;

  ApPermissions = ApPermissions;
  apPermissionData: IApPermissionData;
  private _listMassEditing = [];
  private _subscriptions: Array<Subscription> = [];
  private _applyClicked = new EventEmitter<any>();

  constructor(private formStore: FormStore,
              private farmUsersStore: FarmUsersStore,
              private translationStore: TranslationStore,
              private mapViewStore: MapViewStore,
              private booleanPipe: GetBooleanPipe,
              private permissionPipe: GetPermissionPipe,
              private loginStore: LoginStore) {
    this.mapViewStore.hideMapView();
    this._subscriptions.push(this._applyClicked.subscribe((e) => this._onApplyClicked(e)));
  }

  ngAfterContentInit(): void {
    this.getDataFromStore();
    this.gridBuilder();
    this.fillWizardChoiceData();
  }

  getDataFromStore(): void {
    this.farmUsersLoading$ = this.farmUsersStore.Listen(s => s.loading);
    this._subscriptions.push(this.farmUsersStore.Listen(s => s.data).subscribe(fu => {
      if (fu == null) {
        return;
      }
      const farmUsers = fu.FindAll(_ => _.FarmId === this.loginStore.SelectedFarmId && _.User?.IsConsultant === false && !ApRoleTypeService.hasAgriconRole(_.User));
      this.items = farmUsers.map(users => {
        const userGroups = this.getUserGroups(users.UsergroupUser);
        return {
          Id: users.Id,
          SourceItem: users,
          UserGroups: userGroups,
          Status: users.IsActive ? 1 : -1,
          StatusClass: users.IsActive ? 'ap-status-green' : 'ap-status-red',
          StatusDesc: users.IsActive ? 'Global__Active' : 'Global__Inactive',
          IsDriver: this._boolean(users.IsDriver),
        };
      });
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  getUserGroups(items: IUsergroupUser[]): string[] {
    return items.Convert(i => this.translationStore.FindTranslationForSelectedLanguage(i.Usergroup?.Name));
  }

  fillWizardChoiceData(): void {
    const state = {
      id: UserEdits.IsActive,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global__Status'),
      type: ApGridPagerType.DropDown,
      values: [
        {
          id: UserEditGridActions.ACTIVE,
          label: this.translationStore.FindTranslationForSelectedLanguage('Global__Active')
        },
        {
          id: UserEditGridActions.INACTIVE,
          label: this.translationStore.FindTranslationForSelectedLanguage('Global__Inactive')
        }
      ]
    } as IWizardChoiceData;
    this._listMassEditing.push(state);
    const remove = {
      id: UserEdits.Remove,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global_Remove'),
      type: ApGridPagerType.None,
      values: null,
    } as IWizardChoiceData;
    this._listMassEditing.push(remove);
  }

  onCreate(): void {
    setTimeout(() => this.formStore.openForm({component: ApUsersEditComponent, data: null}), 1);
  }

  onEdit(dataItem): void {
    setTimeout(() => this.formStore.openForm({component: ApUsersEditComponent, data: dataItem.SourceItem}), 1);
  }

  private gridBuilder(): void {
    this.permissionPipe.transform(ApPermissions.EDIT_USERS).subscribe(permission => {
      this.columns = [
        new ApDynGridStatusColumnConfig({
          field: 'Status',
          showHeaderTitle: true,
          class: 'StatusClass',
          description: 'StatusDesc',
          filterable: true,
          sortIndex: 0,
          sortDesc: true,
          groupIndex: 0,
          groupDesc: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.User.Forename',
          title: 'Global__Forename',
          filterable: true,
          width: 50,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.User.Surname',
          title: 'Global__Surname',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.User.UserName',
          title: 'Global__Username',
          filterable: true,
          width: 50,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.User.EMail',
          title: 'Global__Email',
          filterable: true,
          width: 80,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'UserGroups',
          title: 'Global_User_Groups',
          filterable: true,
          width: 80,
          headerFilterable: true,
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'IsDriver',
          title: 'Global__Worker',
          filterable: true,
          width: 50,
          headerFilterable: true,
          checkbox: true,
        })
      ];
      this.pager = new ApDynGridPagerConfig({
        listMassEditing: this._listMassEditing,
        apply: this._applyClicked,
        hasPermission: permission,
      });
    });
  }

  private _onApplyClicked(data: IGridPagerData): void {
    const rows = this.dynComponent.getSelectedItems();
    switch (data.object.id) {
      case UserEdits.IsActive:
        rows.forEach(row => {
          let val = false;
          if (data.value.id === UserEditGridActions.ACTIVE) {
            val = true;
          }
          (row as IFarmUsers).IsActive = val;
          this.farmUsersStore.update(row as IFarmUsers);
        });
        break;
      case UserEdits.Remove:
        rows.forEach(row => {
          this.farmUsersStore.delete(row as IFarmUsers);
        });
        break;
    }
  }

  private _boolean(value: any): boolean {
    return this.booleanPipe.transform(value);
  }
}
